import { Role } from '../types/auth';
import { OrderState } from '../types/orders';

/**
 * Mapeo de roles a estados de orden visibles
 * Define qué estados puede ver cada rol en las tablas y filtros
 *
 * Nota: OperatingTeam no está incluido ya que es un rol no funcional
 *
 * IMPORTANTE: Esta configuración debe estar sincronizada con el backend
 * (rois-back/src/modules/auth/roles/role-state-visibility.ts)
 */
export const ROLE_VISIBLE_STATES: Record<Exclude<Role, Role.OperatingTeam>, OrderState[]> = {
  /**
   * RoomTeam (Trabajador de sala)
   * - Puede ver TODOS los estados en tablas/filtros
   * - Puede crear comunicaciones y anular
   * - NO participa en workflow de trabajo (solo open, managed, annulled)
   */
  [Role.RoomTeam]: Object.values(OrderState),

  /**
   * Tablet (Encargado de tablet)
   * - Ve órdenes en flujo de trabajo activo + abiertas
   * - Puede abrir órdenes para hacer trabajo
   * - NO puede validar (solo prepara para Coex/SuperAdmin)
   * - NO puede anular (solo Coex/SuperAdmin/RoomTeam pueden)
   * - Por defecto carga PendingWork
   */
  [Role.Tablet]: [
    OrderState.Open,
    OrderState.PendingWork,
    OrderState.WorkInProgress,
    OrderState.PendingValidation,
    // Estados propios del Recorregut de Vigilància (2026-08-05) — espejo del
    // backend `auth/roles/role-state-visibility.ts`, donde SÍ muerden: allí
    // `orders.service.findAll` no filtra por tipo y sí interseca con este
    // mapa, así que sin ellos para el Tablet no existía NINGUNA Order en
    // `in-progress`/`paused` (ni la del recorrido ni las de sus tramos).
    // Aquí es inerte hoy — el único lector vivo, `getStateOptionsByRole`,
    // parte de una lista que no los contiene y los añade aparte
    // entity-aware — pero la regla del proyecto es que el fallback del front
    // refleje la autoridad del backend.
    OrderState.InProgress,
    OrderState.Paused,
  ],

  /**
   * Coex (Coordinador de Explotación)
   * - Control total del sistema
   * - Puede validar, anular, cerrar órdenes
   */
  [Role.Coex]: Object.values(OrderState),

  /**
   * Capataz — clon de Coex (ve todos los estados). Sin acceso a budgets.
   */
  [Role.Capataz]: Object.values(OrderState),

  /**
   * SuperAdmin
   * - Acceso completo a todos los estados
   */
  [Role.SuperAdmin]: Object.values(OrderState),
};

/**
 * Obtiene los estados visibles para un rol específico
 *
 * @param role - Rol del usuario
 * @returns Array de estados que el rol puede ver
 *
 * @example
 * ```typescript
 * const states = getVisibleStatesForRole(Role.Tablet);
 * // returns [OrderState.Open, OrderState.PendingWork, ...]
 * ```
 */
export function getVisibleStatesForRole(role: Role): OrderState[] {
  // OperatingTeam no tiene visibilidad (rol no funcional)
  if (role === Role.OperatingTeam) {
    return [];
  }

  return ROLE_VISIBLE_STATES[role as Exclude<Role, Role.OperatingTeam>] || [];
}
